import React, { useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { Button } from "react-bootstrap";
import "./GetStartedPopup.css";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { clearReturnPath } from "../../StateManagement/Reducers/AccountSetupState";

const GetStartedPopup = ({
  title,
  description,
  currentStep,
  totalSteps,
  bullets = [],
  bullets2 = [],
  bullets3 = [],
  bullets4 = [],
  numberedBullets = [],
  bulletheading2,
  bulletheading,
  bullethead,
  onBack,
  onNext,
  onFinish,
  topClose,
  rightClose,
  onGotIt,
  width,
  onClose,
  thanksnote,
  arrowPosition, // 'left-top' | 'left-center' (default = none / use Semantic UI arrow)
  arrowOffsetTop, // Custom top position for the arrow (e.g. 'calc(50% + 55px)')
  showBackdrop = false, // Add option to conditionally show backdrop
  backdropZIndex = 1000,
  nextButtonText,
  closeOnOutsideClick = false,
  preventBackgroundScroll = false, // Add option to prevent background scrolling when hovering over this popup
  children, // Allow rendering custom dynamic content (forms, inputs, etc.)
  disableOverflowHack = false, // Allow opting out of the .app-body overflow hack
}) => {
  // Inject arrow-position override after all stylesheets (guaranteed last-write-wins)
  const ARROW_STYLE_ID = "centerarrow-top-override";
  if (typeof document !== "undefined" && !document.getElementById(ARROW_STYLE_ID)) {
    const el = document.createElement("style");
    el.id = ARROW_STYLE_ID;
    el.textContent =
      ".ui.popup.no-sui-arrow::before { display: none !important; }" +
      ".ui.popup.centerarrowpopup::before { top: 14px !important; margin-top: 0 !important; }";
    document.head.appendChild(el);
  }
  const history = useHistory();
  const popupRef = useRef(null);
  const returnPath = useSelector((state) => state?.returnPath?.path);
  const dispatch = useDispatch();

  useEffect(() => {
    if (disableOverflowHack) return;

    const appBody = document.querySelector(".app-body");

    if (appBody) {
      appBody.style.setProperty("overflow", "unset", "important");
      appBody.style.setProperty("position", "unset", "important");
    }

    return () => {
      if (appBody) {
        appBody.style.removeProperty("overflow");
        appBody.style.removeProperty("position");
      }
    };
  }, [disableOverflowHack]);

  useEffect(() => {
    if (!closeOnOutsideClick || !onClose) return;

    const handleClickOutside = (event) => {
      // If click is inside the popup, do nothing
      if (popupRef.current && popupRef.current.contains(event.target)) {
        return;
      }

      // Ignore clicks inside portaled ant-design dropdowns (like DatePicker "Today" button)
      if (event.target.closest('.ant-picker-dropdown, .ant-select-dropdown')) {
        return;
      }

      // Check if the clicked element is a navigation item (link, tab, etc.)
      const isNavigation = event.target.closest('a, [role="tab"], .nav-link, .nav-item');

      if (isNavigation) {
        // Close the popup when navigating away
        onClose();
      }

      // Note: We deliberately avoid event.preventDefault() and event.stopPropagation()
      // so that other elements (like dropdowns) can still function normally.
    };

    // Use capture phase (true) so we intercept the event before it reaches the target elements
    const timeoutId = setTimeout(() => {
      document.addEventListener("click", handleClickOutside, true);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [closeOnOutsideClick, onClose]);

  // Prevent background scrolling when hovering over the popup, ONLY if requested
  useEffect(() => {
    if (!preventBackgroundScroll) return;

    const handleScrollPrevent = (e) => {
      // Check if the target is inside this popup or its semantic ui wrapper
      let isInsidePopup = false;
      if (popupRef.current) {
        if (popupRef.current.contains(e.target)) {
          isInsidePopup = true;
        } else {
          const wrapper = popupRef.current.closest('.ui.popup');
          if (wrapper && wrapper.contains(e.target)) {
            isInsidePopup = true;
          }
        }
      }

      if (!isInsidePopup) return;

      // If there's a specifically scrollable child inside the popup, allow it to scroll
      const scrollableChild = e.target.closest('.scrollable, .ui.dropdown .menu');
      if (scrollableChild) {
        const style = window.getComputedStyle(scrollableChild);
        if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && scrollableChild.scrollHeight > scrollableChild.clientHeight) {
          const isAtTop = scrollableChild.scrollTop === 0 && e.deltaY < 0;
          const isAtBottom = Math.ceil(scrollableChild.scrollTop + scrollableChild.clientHeight) >= scrollableChild.scrollHeight && e.deltaY > 0;

          if (!isAtTop && !isAtBottom) {
            return; // Allow native scroll
          }
        }
      }

      e.preventDefault();
      e.stopPropagation();
    };

    // Attach listener to document to catch everything (padding, arrows, etc)
    document.addEventListener("wheel", handleScrollPrevent, { passive: false, capture: true });
    document.addEventListener("touchmove", handleScrollPrevent, { passive: false, capture: true });

    return () => {
      document.removeEventListener("wheel", handleScrollPrevent, { capture: true });
      document.removeEventListener("touchmove", handleScrollPrevent, { capture: true });
    };
  }, [preventBackgroundScroll]);

  const handleGotIt = () => {
    if (onGotIt) {
      onGotIt();
      return;
    }
    if (returnPath) {
      history.push(returnPath);
      dispatch(clearReturnPath());
      return;
    }
    history.push({
      pathname: `/schedule/calender`,
      state: { getstartedopen: true },
    });
  };

  return (
    <>
      {showBackdrop && typeof document !== "undefined" &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0, 0, 0, 0.4)",
              zIndex: backdropZIndex,
            }}
          />,
          document.body
        )}
      <div
        ref={popupRef}
        className={`get-started-popup ${topClose ? "top-close" : ""} ${rightClose ? "right-close" : ""}`}
        style={{ width: width, position: "relative" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Custom arrow — bypasses Popper.js which controls Semantic UI's ::before */}
        {arrowPosition === "left-top" && (
          <div
            style={{
              position: "absolute",
              left: "-24px",
              top: "6px",
              width: 0,
              height: 0,
              borderTop: "9px solid transparent",
              borderBottom: "9px solid transparent",
              borderRight: "9px solid #ffffff",
              filter: "drop-shadow(-2px 0px 1px rgba(34,36,38,0.18))",
              zIndex: 10,
            }}
          />
        )}
        {arrowPosition === "left-center" && (
          <div
            style={{
              position: "absolute",
              left: "-24px",
              top: arrowOffsetTop || "50%",
              transform: arrowOffsetTop ? "none" : "translateY(-50%)",
              width: 0,
              height: 0,
              borderTop: "9px solid transparent",
              borderBottom: "9px solid transparent",
              borderRight: "9px solid #ffffff",
              filter: "drop-shadow(-2px 0px 1px rgba(34,36,38,0.18))",
              zIndex: 10,
            }}
          />
        )}
        {arrowPosition === "right-top" && (
          <div
            style={{
              position: "absolute",
              right: "-24px",
              top: "6px",
              width: 0,
              height: 0,
              borderTop: "9px solid transparent",
              borderBottom: "9px solid transparent",
              borderLeft: "9px solid #ffffff",
              filter: "drop-shadow(2px 0px 1px rgba(34,36,38,0.18))",
              zIndex: 10,
            }}
          />
        )}
        {arrowPosition === "right-center" && (
          <div
            style={{
              position: "absolute",
              right: "-24px",
              top: "50%",
              transform: "translateY(-50%)",
              width: 0,
              height: 0,
              borderTop: "9px solid transparent",
              borderBottom: "9px solid transparent",
              borderLeft: "9px solid #ffffff",
              filter: "drop-shadow(2px 0px 1px rgba(34,36,38,0.18))",
              zIndex: 10,
            }}
          />
        )}
        {arrowPosition === "top-right" && (
          <div
            style={{
              position: "absolute",
              right: "24px",
              top: "-20px",
              width: 0,
              height: 0,
              borderLeft: "9px solid transparent",
              borderRight: "9px solid transparent",
              borderBottom: "10px solid #ffffff",
              filter: "drop-shadow(0px -2px 1px rgba(34,36,38,0.18))",
              zIndex: 10,
            }}
          />
        )}
        <div className="popup-header">
          <h4 className="popup-title">{title}</h4>
          {onClose && (
            <span className="close-button" onClick={onClose}>
              ×
            </span>
          )}
        </div>

        <div className="popup-content">
          {typeof description === "string" ? (
            <p className="popup-description">{description}</p>
          ) : (
            <div className="popup-description">{description}</div>
          )}
          {bulletheading && (
            <p className="popup-bullet-heading">{bulletheading}</p>
          )}
          {bullethead && (
            <p className="popup-bullet-head">{bullethead}</p>
          )}
          {bullets.length > 0 && (
            <ul className="popup-bullets">
              {bullets.map((bullet, index) => (
                <li key={index} className="popup-bullet-item">
                  {bullet}
                </li>
              ))}
            </ul>
          )}

          {/* Numbered bullets */}
          {numberedBullets.length > 0 && (
            <ol className="popup-numbered-bullets">
              {numberedBullets.map((bullet, index) => (
                <li key={index} className="popup-number-item">
                  {bullet}
                </li>
              ))}
            </ol>
          )}

          {bullets4.length > 0 && (
            <ol className="popup-bullets">
              {bullets4.map((bullet, index) => (
                <li key={index} className="popup-bullet-item">
                  {bullet}
                </li>
              ))}
            </ol>
          )}

          {bulletheading2 && (
            <p className="popup-bullet-heading">{bulletheading2}</p>
          )}
          {bullets2.length > 0 && (
            <ul className="popup-bullets">
              {bullets2.map((bullet, index) => (
                <li key={index} className="popup-bullet-item">
                  {bullet}
                </li>
              ))}
            </ul>
          )}
          {bullets3.length > 0 && (
            <ol className="popup-orderbullets">
              {bullets3.map((bullet, index) => (
                <li key={index} className="popup-orderbullet-item">
                  {bullet}
                </li>
              ))}
            </ol>
          )}
          {thanksnote && <p>{thanksnote}</p>}

          {/* Render custom dynamic content (forms, inputs, etc.) */}
          {children && <div className="popup-dynamic-content">{children}</div>}
        </div>

        <div className="popup-footer">
          <div className="step-counter">
            {currentStep}/{totalSteps}
          </div>

          <div className="button-group">
            {onBack && (
              <Button
                className="popup-button back-button"
                variant="outline-primary"
                onClick={onBack}
              >
                Back
              </Button>
            )}
            {onNext ? (
              <Button
                className="popup-button next-button"
                variant="primary"
                onClick={onNext}
              >
                {nextButtonText || "Next"}
              </Button>
            ) : onFinish ? (
              <Button
                className="popup-button next-button"
                variant="primary"
                onClick={onFinish}
              >
                {nextButtonText || "Finish"}
              </Button>
            ) : (
              <Button
                className="popup-button next-button"
                variant="primary"
                onClick={handleGotIt}
              >
                Got It
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default GetStartedPopup;